import React, { useEffect, useState } from 'react';
import { listOfNumberInputs } from '../../../../../common/interfaces/aiChatSettings/aiChatSettings.interface';
import {
  AiChatSettingsPerUserService,
  AiChatSettingsService,
  clearFullUserAction,
  clearFullUserListAction,
  FullUserInterface,
  getAllFullUsersAction,
  i18n,
  NavigationService,
  StoreStateInterface,
} from '../../../../../common';
import { Button, Divider, Form, Input, Select, Spin, Tooltip, Radio, notification } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { AdminRoutes } from '../../../_router/admin.routes';
import { useLocation } from 'react-router-dom';
import {
  AiChatSettingsPerUserInterface,
  AiChatSettingsPerUserResponseInterfaceSingle,
} from '../../../../../common/interfaces/aiChatSettings';
import { useDispatch, useSelector } from 'react-redux';

function AiChatSettingsEditPageComponent() {
  const dispatch = useDispatch();
  const location = useLocation();
  const path = location.pathname.split('/');
  const id = parseInt(path[path.length - 1]);
  const isNew = isNaN(id);

  const [aiChatSettings, setAiChatSettings] = useState<TableType>();
  const [isLoading, setIsLoading] = useState(false);

  const [limits, setLimits] = useState<{
    questionNumber: 'unlimited' | 'limited' | 'disabled';
  }>({
    questionNumber: 'disabled',
  });

  const fullUser = useSelector((state: StoreStateInterface) => state.fullUser);

  useEffect(() => {
    localStorage.removeItem('transferFilters');
    dispatch(clearFullUserListAction());
    dispatch(clearFullUserAction());
    dispatch(getAllFullUsersAction(undefined, undefined, 'sort=-id', `page[offset]=0&page[limit]=999999`));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    console.log(fullUser);
  }, [fullUser]);

  useEffect(() => {
    if (isNaN(id)) return;
    AiChatSettingsPerUserService.getSingle(id).subscribe((response) => {
      const getLimitStatus = (limit: number | null) => {
        if (limit === 0) return 'disabled';
        if (limit && limit > 0) return 'limited';
        return 'unlimited';
      };

      setLimits({
        questionNumber: getLimitStatus(response.data.attributes.questionNumber),
      });

      const user = response.included.find((user) => user.id === response.data.relationships.user.data.id);

      setAiChatSettings({
        id: response.data.id,
        user: user!.id!,
        questionNumber: response.data.attributes.questionNumber ?? 0,
      });
    });
  }, [id]);

  const onFinishAiChatSettings = ({ user, questionNumber }: any) => {
    setIsLoading(true);

    const getLimitValue = (limit: 'questionNumber', value: number) => {
      return limits[limit] === 'limited' ? value : limits[limit] === 'unlimited' ? null : 0;
    };

    const body: AiChatSettingsPerUserInterface = {
      id: aiChatSettings?.id,
      type: 'ai_chat_user_monthly_usage',
      attributes: {
        questionNumber: getLimitValue('questionNumber', questionNumber),
        active: true,
      },
      relationships: {
        user: {
          data: {
            id: parseInt(user),
            type: 'user',
          },
        },
      },
    };
    const request = isNew ? AiChatSettingsPerUserService.create : AiChatSettingsPerUserService.update;

    request(body).subscribe(() => {
      setIsLoading(false);
      NavigationService.navigate(AdminRoutes.AI_CHAT_SETTINGS.fullPath);
      setTimeout(() => {
        window.location.reload();
      }, 0);
    }, (error) => {
      setIsLoading(false);
      notification.error({
        message: 'Došlo je do greške prilikom spremanja postavki',
        description: 'Provjerite unos i pokušajte ponovno. Provjerite da li već postoji postavka za ovog korisnika.',
        duration: 10,
      });
    });
  };

  const onFinishAiChatSettingsFailed = (errorInfo: any) => {
    console.log('Failed:', errorInfo);
  };

  if (!aiChatSettings && !isNew)
    return (
      <div className="ai-chat-settings-page w100-h100 center-center">
        <Spin size="large" />
      </div>
    );

  const FormInputLimit = (inputName: string, label: string, type: 'questionNumber') => {
    return (
      <Form.Item key={inputName} label={<span className="text-bold">{label}</span>}>
        <div className="limit-radio-group-container">
          <Radio.Group
            value={limits[type]}
            onChange={(e) => {
              setLimits({
                ...limits,
                [type]: e.target.value,
              });
            }}
            buttonStyle="solid"
          >
            <Radio.Button value="disabled">Isključeno</Radio.Button>
            <Radio.Button value="limited">Ograničeno</Radio.Button>
            <Radio.Button value="unlimited">Neograničeno</Radio.Button>
          </Radio.Group>
          {limits[type] === 'limited' && (
            <Form.Item
              name={inputName}
              rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 12 }}
              style={{ width: '100%', marginBottom: 0 }}
            >
              <Input type="number" placeholder="250" style={{ borderRadius: 0 }} />
            </Form.Item>
          )}
        </div>
      </Form.Item>
    );
  };

  return (
    <div className="ai-chat-settings-page w100-h100">
      <div className="action-container">
        <div className="title">AI Chat postavke po korisniku - {isNew ? 'Kreacija' : 'Uređivanje'}</div>
      </div>

      <Form
        layout="vertical"
        name="aiChatSettingsPerUser"
        size="large"
        scrollToFirstError
        initialValues={aiChatSettings}
        onFinish={onFinishAiChatSettings}
        onFinishFailed={onFinishAiChatSettingsFailed}
        autoComplete="off"
        requiredMark={false}
      >
        <Form.Item
          label={<span className="text-bold">Korisnik</span>}
          name="user"
          rules={[{ required: true, message: i18n.translate(`genericFormMsg.required`) }]}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 12 }}
        >
          <Select
            disabled={!isNew}
            style={{ minWidth: 250 }}
            allowClear
            showSearch
            placeholder="Izaberite korisnika"
            optionFilterProp="children"
            filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={fullUser.fullUsers?.data?.map((x: FullUserInterface) => ({
              value: x.id?.toString(),
              label: x.attributes?.name,
            }))}
          />
        </Form.Item>

        {FormInputLimit('questionNumber', 'Maksimalni broj pitanja mjesečno', 'questionNumber')}

        <Divider />

        <div>
          <Button
            className="button"
            htmlType="submit"
            type="primary"
            size="large"
            style={{ fontSize: 16, fontWeight: 700, borderRadius: 0, minWidth: 193, marginBottom: 24 }}
            loading={isLoading}
          >
            {i18n.translate('genericButtons.save')}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default AiChatSettingsEditPageComponent;

interface TableType {
  id?: number;
  user: number;
  questionNumber: number;
}
